import PropTypes, { shape } from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { errorClear, errorShow } from '../../Utility/Form'
import DatePicker from "react-datepicker"

import "react-datepicker/dist/react-datepicker.min.css";

/**
 * Primary UI component for user interaction
 */
export const FormDatePicker = ({ label, name, required, step, type, validationHash, validationMessages, value, minDate, maxDate, minAge, dateFormat, placeholder, ...props }) => {
  const [date, setDate] = useState(value)
  const [inputValue, inputValueSet] = useState(value)
  const [inputIsInvalid, inputIsInvalidSet] = useState(false)
  const [hasInitialised, hasInitialisedSet] = useState(false)
  const input = useRef(null)

  /**
   * The error ID.
   *
   * @type {string}
   */
  const errorId = `${name}_error`

  /**
   * The error message class
   *
   * @type {string}
   */
  const errorSpan = 'b-form-input__error-message'

  /**
   * Handle any form changes.
   *
   * @param {Date} date
   *   The new date
   */
  const datepickerInputChange = (date) => {
    // Reset any custom error messages.
    if (input.current.input) {
      input.current.input.setCustomValidity('')
    }

    // Only set field value if it's in the mm/yyyy or m/yyyy format. eg.: 12/2010 or 1/2023
    if (input.current.input.value.match(/[0-9]+\/[0-9][0-9][0-9][0-9]/g) || input.current.input.value === '') {
      setDate(date)
      inputValueSet(formatDate(date, dateFormat))
    }
  }
  /**
   *
   * Prepare the requested date format
   *
   * @param {Date} date
   * @param {string} dateFormat
   *
   * @return {string} The formatted date
   */
  const formatDate = (date, dateFormat) => {
    if (date) {
      const y = date.getFullYear();
      let m = date.getMonth() + 1

      switch(dateFormat) {
        case 'MM/yyyy':
          if (m < 10) {
            m = '0' + m
          }
          return `${m}/${y}`
        case 'MM/yy':
          if (m < 10) {
            m = '0' + m
          }
          return `${m}/${y.toString().substring(2)}`
        case 'dd/MM/yyyy':
          return date.toLocaleDateString('en-GB')
        default:
          return date.toISOString();
      }
    }
    else {
      return ''
    }



  }

  /**
   * Validate the element.
   */
  const validate = () => {
    if (input.current.input.validity.valid) {
      errorClear(input.current.input, `.${errorSpan}`)
      inputIsInvalidSet(false)
    }
    else {
      errorShow(input.current.input, `.${errorSpan}`, validationAttributes['data-error-valuemissing-message'])
      inputIsInvalidSet(true)
    }
  }

  // Check if the date is older than 13 years.
  // date is in mm/yy format
  const checkMinAge = (date, minAge) => {
    const today = new Date()
    if(date) {
      let age = today.getFullYear() - date.getFullYear()
      const m = today.getMonth() - date.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
        age--
      }
      return age >= minAge
    }

    return true
  }

  // validate minimum age
  const validateMinAge = (date, minAge) => {
    if (checkMinAge(date, minAge)) {
      errorClear(input.current.input, `.${errorSpan}`)
      inputIsInvalidSet(false)
    } else {
      errorShow(input.current.input, `.${errorSpan}`, 'We’re currently unable to complete gifts online for under-13s. Please call our Supporter Care team on 0300 303 9876 (option 3)')
      inputIsInvalidSet(true)
    }
  }

  /**
   * Generate the validation attributes.
   *
   * @type {{}}
   */
  const validationAttributes = validationMessages ? validationMessages.reduce((validationAttributes, current) => {
    if (current.message) {
      validationAttributes[`data-error-${current.type.toLowerCase()}-message`] = current.message
    }

    if (current.type === 'mindate') {
      validationAttributes['minDate'] = current.minDate
    }

    if (current.type === 'maxdate') {
      validationAttributes['maxDate'] = current.maxDate
    }

    return validationAttributes
  }, {}) : {}


  useEffect(() => {
    if (hasInitialised) {
      validate()
      if (inputValue) {
        validateMinAge(date, minAge)
      }
      const handler = inputIsInvalid ? null : setTimeout(() => props.onInputChange(name, inputValue), 300)
      return () => clearTimeout(handler)
    }
  }, [inputValue, inputIsInvalid])

  useEffect(() => {
    if (validationHash && hasInitialised) {
      validate()
      if (inputValue) {
        validateMinAge(date, minAge)
      }
    }
  }, [inputValue, validationHash])

  useEffect(() => {
    hasInitialisedSet(true)
    // set input mode to prevent keyboard popup on mobile devices.
    if (input.current && input.current.input) {
      input.current.input.setAttribute('inputmode','none')
    }
  }, [])

  useEffect(() => {
    if (value !== inputValue) {
      inputValueSet(value)
    }
  }, [value])

  return (
    <div className={'b-form-row'}>
      <div className="b-form-input" data-type={'date'} data-state={inputIsInvalid ? 'error' : 'ok'}>
        <label htmlFor={name}>{label} {required ? '*' : null}</label>
        <DatePicker
          ref={input}
          selected={date}
          dateFormat={dateFormat}
          placeholderText={placeholder}
          showMonthYearPicker
          required={required}
          onChange={datepickerInputChange}
          name={name}
          id={name}
          minDate={minDate}
          maxDate={maxDate}
          inputProps={{inputmode: 'none', readOnly: true}}
          />
        {required || Object.keys(validationAttributes).length > 0 ?
          <div className="b-form-input__error-wrapper">
            <span className={errorSpan} id={errorId} aria-live="assertive" />
          </div> : null}
      </div>
    </div>
  )
}

FormDatePicker.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  step: PropTypes.string,
  validationHash: PropTypes.string,
  validationMessages: PropTypes.arrayOf(
    shape({
      type: PropTypes.string,
      message: PropTypes.string,
      pattern: PropTypes.string,
    })
  ),
  value: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  placeholder: PropTypes.string,
}
